import { useState } from 'react'
import { FileText, Search, MoreVertical, Edit, Copy, Trash2, Eye, Send } from 'lucide-react'
import DOMPurify from 'dompurify'
import { useTemplates, useDeleteTemplate, useDuplicateTemplate } from '@hooks/useTemplates'
import { Template } from '@/types'
import { formatDateTime } from '@lib/utils'
import { TemplateFilters } from '@services/template.service'

interface TemplateListProps {
  onEdit: (template: Template) => void
  onPreview: (template: Template) => void
  onTest: (template: Template) => void
}

export function TemplateList({ onEdit, onPreview, onTest }: TemplateListProps) {
  const [filters, setFilters] = useState<TemplateFilters>({
    page: 1,
    limit: 12,
  })
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isLoading, error } = useTemplates(filters)
  const deleteMutation = useDeleteTemplate()
  const duplicateMutation = useDuplicateTemplate()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // Only set search if non-empty, otherwise remove it
    const newFilters = { ...filters, page: 1 }
    if (searchTerm.trim()) {
      newFilters.search = searchTerm.trim()
    } else {
      delete newFilters.search
    }
    setFilters(newFilters)
  }

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this template?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const handleDuplicate = async (template: Template) => {
    const name = prompt('Enter a name for the duplicate template:', `${template.name} (Copy)`)
    if (name) {
      await duplicateMutation.mutateAsync({ id: template.id, data: { name } })
    }
  }

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-6 text-center">
        <p className="text-rose-400">Failed to load templates</p>
      </div>
    )
  }

  const templates = data?.data || []
  const pagination = data?.pagination

  return (
    <div className="space-y-4">
      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search templates by name or subject..."
            className="input-modern pl-10"
          />
        </div>
        <button
          type="submit"
          className="btn-primary"
        >
          Search
        </button>
      </form>

      {/* Template Grid */}
      {templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center stat-card p-12">
          <FileText className="mb-4 h-16 w-16 text-slate-600" />
          <h3 className="text-lg font-semibold text-slate-200">No templates found</h3>
          <p className="mt-2 text-sm text-slate-500">
            {filters.search ? 'Try adjusting your search' : 'Create your first template to get started'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => onEdit(template)}
              onPreview={() => onPreview(template)}
              onTest={() => onTest(template)}
              onDuplicate={() => handleDuplicate(template)}
              onDelete={() => handleDelete(template.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between stat-card px-6 py-4">
          <p className="text-sm text-slate-400">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
              className="btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.totalPages}
              className="btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

interface TemplateCardProps {
  template: Template
  onEdit: () => void
  onPreview: () => void
  onTest: () => void
  onDuplicate: () => void
  onDelete: () => void
}

function TemplateCard({ template, onEdit, onPreview, onTest, onDuplicate, onDelete }: TemplateCardProps) {
  const [showMenu, setShowMenu] = useState(false)

  return (
    <div className="group relative stat-card transition-all hover:border-slate-600/50">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-slate-100">{template.name}</h3>
          <p className="mt-1 text-sm text-slate-400">{template.subject}</p>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
          >
            <MoreVertical size={18} />
          </button>
          {showMenu && (
            <div className="absolute right-0 z-50 mt-2 w-48 rounded-xl bg-slate-800 border border-slate-700 shadow-xl shadow-black/20 overflow-hidden">
              <button
                onClick={() => {
                  onEdit()
                  setShowMenu(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
              >
                <Edit size={16} className="text-cyan-400" /> Edit Template
              </button>
              <button
                onClick={() => {
                  onPreview()
                  setShowMenu(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
              >
                <Eye size={16} className="text-indigo-400" /> Preview
              </button>
              <button
                onClick={() => {
                  onTest()
                  setShowMenu(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
              >
                <Send size={16} className="text-emerald-400" /> Send Test
              </button>
              <button
                onClick={() => {
                  onDuplicate()
                  setShowMenu(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
              >
                <Copy size={16} className="text-amber-400" /> Duplicate
              </button>
              <div className="border-t border-slate-700">
                <button
                  onClick={() => {
                    onDelete()
                    setShowMenu(false)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-rose-400 hover:bg-rose-500/10 transition-colors"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="mb-4 h-32 overflow-hidden rounded-xl border border-slate-700/50 bg-slate-800/50">
        <div
          className="scale-[0.3] origin-top-left"
          style={{ width: '333%', height: '333%' }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.bodyHtml || '') }}
        />
      </div>

      {/* Variables */}
      {template.variables && template.variables.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-slate-500">Variables:</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {template.variables.slice(0, 3).map((variable) => (
              <span
                key={variable}
                className="inline-flex rounded-full bg-cyan-500/15 px-2.5 py-0.5 text-xs font-medium text-cyan-400 border border-cyan-500/20"
              >
                {variable}
              </span>
            ))}
            {template.variables.length > 3 && (
              <span className="inline-flex items-center text-xs text-slate-500">
                +{template.variables.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-slate-700/50 pt-4">
        <p className="text-xs text-slate-500">{formatDateTime(template.updatedAt)}</p>
        <div className="flex gap-2">
          <button
            onClick={onEdit}
            className="btn-primary text-xs px-3 py-1.5"
          >
            Edit
          </button>
          <button
            onClick={onPreview}
            className="btn-secondary text-xs px-3 py-1.5"
          >
            Preview
          </button>
        </div>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="stat-card">
          <div className="mb-4 space-y-2">
            <div className="h-4 w-2/3 skeleton rounded" />
            <div className="h-3 w-full skeleton rounded" />
          </div>
          <div className="mb-4 h-32 skeleton rounded-xl" />
          <div className="h-8 skeleton rounded" />
        </div>
      ))}
    </div>
  )
}
